import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import DictationInput from '../components/DictationInput';
import {
  FileText, Clock, CheckCircle2, DollarSign, Plus, Filter,
  BarChart3, Shield, RefreshCw, Eye, Briefcase, ArrowUpRight, Edit, ChevronDown, ChevronUp, X
} from 'lucide-react';

const SERVICE_TYPES = [
  { value: 'peer_support_contact', label: 'Peer Support Contact' },
  { value: 'group_session', label: 'Group Session' },
  { value: 'employment_support', label: 'Employment Support' },
  { value: 'housing_support', label: 'Housing Support' },
  { value: 'referral_coordination', label: 'Referral Coordination' },
  { value: 'transportation_assistance', label: 'Transportation Assistance' },
  { value: 'document_assistance', label: 'Document Assistance' },
  { value: 'intake', label: 'Intake' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'case_management', label: 'Case Management' },
  { value: 'crisis_intervention', label: 'Crisis Intervention' },
];

const BILLABLE_OPTIONS = [
  { value: 'billable', label: 'Billable' },
  { value: 'non_billable', label: 'Non-Billable' },
  { value: 'pro_bono', label: 'Pro Bono' },
];

const STATUS_COLORS = {
  unverified: 'bg-slate-100 text-slate-600',
  verified: 'bg-emerald-50 text-emerald-700',
  verified_billable: 'bg-teal-50 text-teal-700',
  rejected: 'bg-red-50 text-red-700',
};

export default function ServiceLedgerPage() {
  const { axiosAuth, user } = useAuth();
  const [tab, setTab] = useState('services');
  const [services, setServices] = useState([]);
  const [fundingSources, setFundingSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [fundingOpen, setFundingOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [clients, setClients] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const [form, setForm] = useState({
    participant_id: '', service_type: '', date: new Date().toISOString().slice(0, 10),
    start_time: '09:00', duration_minutes: 30, location: 'office', notes: '', summary: '',
    billable_status: 'billable', funding_source_id: ''
  });

  const [fundingForm, setFundingForm] = useState({
    name: '', type: 'grant', funder_name: '', start_date: '', end_date: '',
    budget_amount: 0, covered_service_types: [], covered_programs: [],
    reporting_requirements: '', billing_codes: []
  });

  const isAdmin = ['coordinator', 'admin', 'network_admin'].includes(user?.role);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const api = axiosAuth();
      const params = {};
      if (filterType !== 'all') params.service_type = filterType;
      if (filterStatus !== 'all') params.verification_status = filterStatus;

      const [svcRes, fundRes] = await Promise.all([
        api.get('/services', { params }),
        isAdmin ? api.get('/funding-sources') : Promise.resolve({ data: [] })
      ]);
      setServices(svcRes.data);
      setFundingSources(fundRes.data);

      // Load clients list for the create form
      try {
        const clientsRes = await api.get('/users?role=client');
        setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
      } catch { setClients([]); }
    } catch {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, filterType, filterStatus, isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

      const createService = async () => {
    try {
      const api = axiosAuth();
      await api.post('/services', {
        ...form,
        duration_minutes: parseInt(form.duration_minutes),
        funding_source_id: form.funding_source_id || null,
        tags: []
      });
      toast.success('Service entry created');
      setCreateOpen(false);
      setForm({ participant_id: '', service_type: '', date: new Date().toISOString().slice(0, 10),
        start_time: '09:00', duration_minutes: 30, location: 'office', notes: '', summary: '',
        billable_status: 'billable', funding_source_id: '' });
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create service');
    }
  };

  const startEditService = (svc) => {
    setEditingId(svc.id);
    setForm({
      participant_id: svc.participant_id || '',
      service_type: svc.service_type || '',
      date: svc.date || new Date().toISOString().slice(0, 10),
      start_time: svc.start_time || '09:00',
      duration_minutes: svc.duration_minutes || 30,
      location: svc.location || 'office',
      notes: svc.notes || '',
      summary: svc.summary || '',
      billable_status: svc.billable_status || 'billable',
      funding_source_id: svc.funding_source_id || ''
    });
  };

  const updateService = async (svcId) => {
    try {
      const api = axiosAuth();
      await api.put(`/services/${svcId}`, {
        ...form,
        duration_minutes: parseInt(form.duration_minutes),
        funding_source_id: form.funding_source_id || null,
      });
      toast.success('Service entry updated');
      setEditingId(null);
      setForm({ participant_id: '', service_type: '', date: new Date().toISOString().slice(0, 10),
        start_time: '09:00', duration_minutes: 30, location: 'office', notes: '', summary: '',
        billable_status: 'billable', funding_source_id: '' });
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update service');
    }
  };

  const verifyService = async (status) => {
    if (!selectedService) return;
    try {
      const api = axiosAuth();
      await api.post(`/services/${selectedService.id}/verify`, {
        verification_status: status, verification_notes: ''
      });
      toast.success(`Service ${status}`);
      setVerifyOpen(false);
      setSelectedService(null);
      loadData();
    } catch {
      toast.error('Failed to verify service');
    }
  };

  const createFundingSource = async () => {
    try {
      const api = axiosAuth();
      await api.post('/funding-sources', {
        ...fundingForm,
        budget_amount: parseFloat(fundingForm.budget_amount) || 0,
        billing_codes: fundingForm.billing_codes.length ? fundingForm.billing_codes : []
      });
      toast.success('Funding source created');
      setFundingOpen(false);
      setFundingForm({ name: '', type: 'grant', funder_name: '', start_date: '', end_date: '',
        budget_amount: 0, covered_service_types: [], covered_programs: [],
        reporting_requirements: '', billing_codes: [] });
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create funding source');
    }
  };

  // Stats (memoized)
  const totalHours = useMemo(() => (services.reduce((a, s) => a + (s.duration_minutes || 0), 0) / 60).toFixed(1), [services]);
  const verified = useMemo(() => services.filter(s => s.verification_status === 'verified').length, [services]);
  const billable = useMemo(() => services.filter(s => ['billable', 'verified_billable'].includes(s.billable_status)).length, [services]);
  const pendingEdits = useMemo(() => services.filter(s => s.edit_approval_status === 'pending_approval').length, [services]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="service-ledger-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Service Ledger
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Track, verify, and manage service delivery</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} className="rounded-full gap-2" data-testid="refresh-services-btn">
            <RefreshCw size={14} /> Refresh
          </Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full gap-2 bg-teal-600 hover:bg-teal-700" data-testid="log-service-btn">
                <Plus size={14} /> Log Service
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-lg">
              <DialogHeader><DialogTitle>Log Service Entry</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2 max-h-[60vh] overflow-y-auto">
                <div className="space-y-1.5">
                  <Label>Participant</Label>
                  <Select value={form.participant_id} onValueChange={v => setForm(f => ({ ...f, participant_id: v }))}>
                    <SelectTrigger data-testid="service-participant-select"><SelectValue placeholder="Select participant" /></SelectTrigger>
                    <SelectContent>
                      {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Service Type</Label>
                  <Select value={form.service_type} onValueChange={v => setForm(f => ({ ...f, service_type: v }))}>
                    <SelectTrigger data-testid="service-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label>Date</Label>
                    <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="service-date-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Start Time</Label>
                    <Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} data-testid="service-start-time-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Duration (min)</Label>
                    <Input type="number" value={form.duration_minutes} onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))} data-testid="service-duration-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Location</Label>
                    <Select value={form.location} onValueChange={v => setForm(f => ({ ...f, location: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="office">Office</SelectItem>
                        <SelectItem value="field">Field</SelectItem>
                        <SelectItem value="virtual">Virtual</SelectItem>
                        <SelectItem value="community">Community</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Billable Status</Label>
                    <Select value={form.billable_status} onValueChange={v => setForm(f => ({ ...f, billable_status: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {BILLABLE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {fundingSources.length > 0 && (
                  <div className="space-y-1.5">
                    <Label>Funding Source</Label>
                    <Select value={form.funding_source_id} onValueChange={v => setForm(f => ({ ...f, funding_source_id: v }))}>
                      <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                      <SelectContent>
                        {fundingSources.map(fs => <SelectItem key={fs.id} value={fs.id}>{fs.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>Brief Summary</Label>
                  <DictationInput value={form.summary} onChange={(val) => setForm(f => ({ ...f, summary: val }))} placeholder="Brief summary..." rows={2} showCleanup={false} data-testid="service-summary-dictation" />
                </div>
                <div className="space-y-1.5">
                  <Label>Detailed Notes</Label>
                  <DictationInput value={form.notes} onChange={(val) => setForm(f => ({ ...f, notes: val }))} placeholder="Detailed notes..." rows={3} data-testid="service-notes-dictation" />
                </div>
                <Button onClick={createService} className="w-full bg-teal-600 hover:bg-teal-700 rounded-full" data-testid="submit-service-btn">
                  Log Service
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats — click to filter */}
      <p className="text-xs text-muted-foreground flex items-center gap-1 -mb-1" data-testid="ledger-filter-hint">Tap a card to filter the list below</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card onClick={() => { setFilterType('all'); setFilterStatus('all'); }} className={`border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-all ${filterStatus === 'all' && filterType === 'all' ? 'ring-2 ring-teal-500' : ''}`} data-testid="card-total-services">
          <CardContent className="p-4 text-center">
            <FileText size={22} className="mx-auto mb-1.5 text-teal-600" />
            <p className="text-2xl font-bold" data-testid="total-services-count">{services.length}</p>
            <p className="text-xs text-muted-foreground">Total Services</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl" data-testid="card-total-hours">
          <CardContent className="p-4 text-center">
            <Clock size={22} className="mx-auto mb-1.5 text-blue-600" />
            <p className="text-2xl font-bold">{totalHours}h</p>
            <p className="text-xs text-muted-foreground">Total Hours</p>
          </CardContent>
        </Card>
        <Card onClick={() => setFilterStatus('verified')} className={`border-0 shadow-sm rounded-xl bg-emerald-50 cursor-pointer hover:shadow-md transition-all ${filterStatus === 'verified' ? 'ring-2 ring-emerald-500' : ''}`} data-testid="card-verified">
          <CardContent className="p-4 text-center">
            <CheckCircle2 size={22} className="mx-auto mb-1.5 text-emerald-600" />
            <p className="text-2xl font-bold text-emerald-700">{verified}</p>
            <p className="text-xs text-emerald-600">Verified</p>
          </CardContent>
        </Card>
        <Card onClick={() => setFilterStatus('verified_billable')} className={`border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-all ${filterStatus === 'verified_billable' ? 'ring-2 ring-amber-500' : ''}`} data-testid="card-billable">
          <CardContent className="p-4 text-center">
            <DollarSign size={22} className="mx-auto mb-1.5 text-amber-600" />
            <p className="text-2xl font-bold">{billable}</p>
            <p className="text-xs text-muted-foreground">Billable</p>
          </CardContent>
        </Card>
        {isAdmin && pendingEdits > 0 && (
          <Card className="border-0 shadow-sm rounded-xl border-violet-100">
            <CardContent className="p-4 text-center">
              <Edit size={22} className="mx-auto mb-1.5 text-violet-600" />
              <p className="text-2xl font-bold text-violet-700">{pendingEdits}</p>
              <p className="text-xs text-violet-600">Edits Pending Approval</p>
            </CardContent>
          </Card>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 h-auto p-1 bg-slate-100 rounded-xl max-w-xs">
          <TabsTrigger value="services" className="rounded-lg text-xs py-2 data-[state=active]:bg-white">
            <FileText size={14} className="mr-1" /> Services
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="funding" className="rounded-lg text-xs py-2 data-[state=active]:bg-white">
              <DollarSign size={14} className="mr-1" /> Funding
            </TabsTrigger>
          )}
        </TabsList>

        {/* Services Tab */}
        <TabsContent value="services" className="mt-4">
          {/* Filters */}
          <div className="flex gap-2 mb-4 flex-wrap">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48 rounded-full" data-testid="filter-service-type">
                <Filter size={14} className="mr-1" /><SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {SERVICE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-44 rounded-full" data-testid="filter-verification-status">
                <Shield size={14} className="mr-1" /><SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="unverified">Unverified</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            {services.length === 0 ? (
              <div className="text-center py-12">
                <FileText size={40} className="mx-auto text-slate-200 mb-3" />
                <p className="text-sm text-muted-foreground">No services recorded yet</p>
              </div>
            ) : services.map(svc => (
              <Card key={svc.id} className="border-0 shadow-sm rounded-xl overflow-hidden" data-testid={`service-entry-${svc.id}`}>
                <CardContent className="p-0">
                  <div className="p-4 cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => setExpandedId(expandedId === svc.id ? null : svc.id)}>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                        <Briefcase size={18} className="text-teal-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{SERVICE_TYPES.find(t => t.value === svc.service_type)?.label || svc.service_type}</span>
                          <Badge className={`${STATUS_COLORS[svc.verification_status] || STATUS_COLORS.unverified} border-0 text-xs`}>
                            {svc.verification_status}
                          </Badge>
                          {svc.billable_status === 'billable' && <Badge className="bg-amber-50 text-amber-600 border-0 text-xs">Billable</Badge>}
                          {svc.edit_approval_status === 'pending_approval' && <Badge className="bg-violet-50 text-violet-700 border-0 text-xs">Edit Pending Approval</Badge>}
                          {svc.edit_approval_status === 'rejected' && <Badge className="bg-red-50 text-red-700 border-0 text-xs">Edit Rejected</Badge>}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span>{svc.date}</span>
                          {svc.start_time && <span>{svc.start_time}</span>}
                          <span>{svc.duration_minutes} min</span>
                          <span className="capitalize">{svc.location}</span>
                          {svc.summary && <span className="truncate max-w-[200px]">{svc.summary}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {isAdmin && svc.verification_status === 'unverified' && (
                          <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={(e) => { e.stopPropagation(); setSelectedService(svc); setVerifyOpen(true); }} data-testid={`verify-btn-${svc.id}`}>
                            <Shield size={12} /> Verify
                          </Button>
                        )}
                        {isAdmin && svc.edit_approval_status === 'pending_approval' && (
                          <Button size="sm" variant="outline" className="rounded-full gap-1 border-violet-200 text-violet-700" onClick={(e) => {
                            e.stopPropagation();
                            const api = axiosAuth();
                            api.post(`/services/${svc.id}/approve-edit`, { decision: 'approved' })
                              .then(() => { toast.success('Edit approved'); loadData(); })
                              .catch(() => toast.error('Failed to approve edit'));
                          }} data-testid={`approve-edit-btn-${svc.id}`}>
                            <CheckCircle2 size={12} /> Approve Edit
                          </Button>
                        )}
                        {expandedId === svc.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </div>
                    </div>
                  </div>
                  {expandedId === svc.id && (
                    <div className="px-4 pb-4 border-t space-y-3 animate-in fade-in-0 duration-200">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs pt-3">
                        <div className="bg-muted/30 rounded-lg p-2"><span className="text-muted-foreground block">Date</span><span className="font-medium">{svc.date}</span></div>
                        {svc.start_time && <div className="bg-muted/30 rounded-lg p-2"><span className="text-muted-foreground block">Start Time</span><span className="font-medium">{svc.start_time}</span></div>}
                        <div className="bg-muted/30 rounded-lg p-2"><span className="text-muted-foreground block">Duration</span><span className="font-medium">{svc.duration_minutes} min</span></div>
                        <div className="bg-muted/30 rounded-lg p-2"><span className="text-muted-foreground block">Location</span><span className="font-medium capitalize">{svc.location}</span></div>
                      </div>
                      {svc.summary && <div className="bg-sky-50 rounded-lg p-3 text-sm border border-sky-100"><p className="text-xs font-semibold text-sky-600 mb-1">Summary</p>{svc.summary}</div>}
                      {svc.notes && <div className="bg-muted/30 rounded-lg p-3 text-sm"><p className="text-xs font-semibold text-muted-foreground mb-1">Detailed Notes</p>{svc.notes}</div>}
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="rounded-full gap-1 text-xs" onClick={() => startEditService(svc)} data-testid={`edit-service-${svc.id}`}>
                          <Edit size={12} /> Edit
                        </Button>
                      </div>
                    </div>
                  )}
                  {editingId === svc.id && (
                    <div className="px-4 pb-4 border-t space-y-3 animate-in fade-in-0 duration-200 bg-teal-50/30">
                      <div className="flex items-center justify-between pt-3">
                        <p className="text-sm font-semibold">Editing Service Entry</p>
                        <Button variant="ghost" size="sm" onClick={() => { setEditingId(null); }}><X size={14} /></Button>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1"><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="h-8 text-xs" /></div>
                        <div className="space-y-1"><Label className="text-xs">Start Time</Label><Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} className="h-8 text-xs" /></div>
                        <div className="space-y-1"><Label className="text-xs">Duration (min)</Label><Input type="number" value={form.duration_minutes} onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))} className="h-8 text-xs" /></div>
                      </div>
                      <div className="space-y-1"><Label className="text-xs">Brief Summary</Label><DictationInput value={form.summary} onChange={(val) => setForm(f => ({ ...f, summary: val }))} placeholder="Brief summary..." rows={2} showCleanup={false} data-testid="edit-summary-dictation" /></div>
                      <div className="space-y-1"><Label className="text-xs">Detailed Notes</Label><DictationInput value={form.notes} onChange={(val) => setForm(f => ({ ...f, notes: val }))} placeholder="Detailed notes..." rows={3} data-testid="edit-notes-dictation" /></div>
                      <Button size="sm" className="bg-teal-600 hover:bg-teal-700 rounded-full gap-1 text-xs" onClick={() => updateService(svc.id)} data-testid={`save-edit-${svc.id}`}>Save Changes</Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Funding Tab */}
        {isAdmin && (
          <TabsContent value="funding" className="mt-4">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm font-medium text-muted-foreground">{fundingSources.length} funding source(s)</p>
              <Dialog open={fundingOpen} onOpenChange={setFundingOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full gap-2 bg-teal-600 hover:bg-teal-700" data-testid="add-funding-btn">
                    <Plus size={14} /> Add Funding Source
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl">
                  <DialogHeader><DialogTitle>Add Funding Source</DialogTitle></DialogHeader>
                  <div className="space-y-3 pt-2">
                    <div className="space-y-1.5">
                      <Label>Name</Label>
                      <Input value={fundingForm.name} onChange={e => setFundingForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g., SAMHSA Grant 2026" data-testid="funding-name-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Type</Label>
                        <Select value={fundingForm.type} onValueChange={v => setFundingForm(f => ({ ...f, type: v }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="grant">Grant</SelectItem>
                            <SelectItem value="contract">Contract</SelectItem>
                            <SelectItem value="medicaid">Medicaid</SelectItem>
                            <SelectItem value="private">Private Pay</SelectItem>
                            <SelectItem value="donation">Donation</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Funder Name</Label>
                        <Input value={fundingForm.funder_name} onChange={e => setFundingForm(f => ({ ...f, funder_name: e.target.value }))} placeholder="Organization" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Start Date</Label>
                        <Input type="date" value={fundingForm.start_date} onChange={e => setFundingForm(f => ({ ...f, start_date: e.target.value }))} />
                      </div>
                      <div className="space-y-1.5">
                        <Label>End Date</Label>
                        <Input type="date" value={fundingForm.end_date} onChange={e => setFundingForm(f => ({ ...f, end_date: e.target.value }))} />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Budget Amount ($)</Label>
                      <Input type="number" value={fundingForm.budget_amount} onChange={e => setFundingForm(f => ({ ...f, budget_amount: e.target.value }))} data-testid="funding-budget-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Reporting Requirements</Label>
                      <DictationInput value={fundingForm.reporting_requirements} onChange={e => setFundingForm(f => ({ ...f, reporting_requirements: e.target.value }))} placeholder="Quarterly reports..." rows={2} showCleanup={false} />
                    </div>
                    <Button onClick={createFundingSource} className="w-full bg-teal-600 hover:bg-teal-700 rounded-full" data-testid="submit-funding-btn">
                      Create Funding Source
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-3">
              {fundingSources.length === 0 ? (
                <div className="text-center py-12">
                  <DollarSign size={40} className="mx-auto text-slate-200 mb-3" />
                  <p className="text-sm text-muted-foreground">No funding sources configured</p>
                </div>
              ) : fundingSources.map(fs => (
                <Card key={fs.id} className="border-0 shadow-sm rounded-xl" data-testid={`funding-source-${fs.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                        <DollarSign size={18} className="text-emerald-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm">{fs.name}</span>
                          <Badge className="bg-teal-50 text-teal-700 border-0 text-xs capitalize">{fs.type}</Badge>
                          <Badge className={`border-0 text-xs ${fs.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{fs.status}</Badge>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span>{fs.funder_name}</span>
                          {fs.start_date && <span>{fs.start_date} - {fs.end_date || 'ongoing'}</span>}
                          {fs.budget_amount > 0 && <span className="font-medium text-emerald-600">${fs.budget_amount.toLocaleString()}</span>}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Verify Dialog */}
      <Dialog open={verifyOpen} onOpenChange={v => { setVerifyOpen(v); if (!v) setSelectedService(null); }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Verify Service Entry</DialogTitle></DialogHeader>
          {selectedService && (
            <div className="space-y-4 pt-2">
              <div className="p-3 rounded-xl bg-slate-50 space-y-1">
                <p className="text-xs text-muted-foreground">Service</p>
                <p className="font-medium">{SERVICE_TYPES.find(t => t.value === selectedService.service_type)?.label}</p>
                <p className="text-sm text-muted-foreground">{selectedService.date} - {selectedService.duration_minutes} min</p>
                {selectedService.summary && <p className="text-sm">{selectedService.summary}</p>}
              </div>
              <div className="flex gap-2">
                <Button onClick={() => verifyService('verified')} className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-full gap-1" data-testid="approve-verify-btn">
                  <CheckCircle2 size={14} /> Verify
                </Button>
                <Button onClick={() => verifyService('rejected')} variant="outline" className="flex-1 rounded-full gap-1 border-red-300 text-red-600 hover:bg-red-50" data-testid="reject-verify-btn">
                  Reject
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
